package link.erpango.quip.features.transfer

import kotlinx.datetime.TimeZone
import kotlinx.datetime.number
import kotlinx.datetime.toLocalDateTime
import link.erpango.quip.domain.entities.QRData
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

/**
 * Utilidades puras para los códigos de transferencia de vehículos.
 *
 * Hay dos formas de identificar una transferencia:
 * - el token largo (`secrets.token_urlsafe`), que va dentro del QR y distingue mayúsculas;
 * - el OTP corto de 8 caracteres (`ABCD-EFGH`) para introducirlo a mano.
 *
 * Solo el OTP se normaliza a mayúsculas: el token largo debe enviarse tal cual.
 */
object TransferCode {

    /** Ejemplo que se muestra como placeholder. Mismo formato que el backend. */
    const val OTP_EXAMPLE = "ABCD-2345"

    private const val OTP_LENGTH = 8
    private val separators = Regex("[\\s-]+")

    private fun compact(raw: String): String = raw.replace(separators, "")

    /** true si, ignorando espacios y guiones, son exactamente 8 caracteres alfanuméricos. */
    fun isOtpShaped(raw: String): Boolean {
        val compacted = compact(raw)
        return compacted.length == OTP_LENGTH && compacted.all { it.isAsciiLetterOrDigit() }
    }

    /** `" abcd 2345 "` -> `"ABCD-2345"`. Devuelve null si no tiene forma de OTP. */
    fun normalizeOtp(raw: String): String? {
        if (!isOtpShaped(raw)) return null
        val upper = compact(raw).uppercase()
        return upper.substring(0, 4) + "-" + upper.substring(4)
    }

    /**
     * Interpreta lo que el usuario escribe o pega a mano:
     * - JSON de un QR de transferencia -> su `token`;
     * - código con forma de OTP -> OTP normalizado (`ABCD-2345`);
     * - cualquier otra cosa -> el texto recortado (p. ej. un token largo pegado), sin tocar mayúsculas.
     * Devuelve null si está vacío.
     */
    fun resolveManualInput(raw: String): String? {
        val trimmed = raw.trim()
        if (trimmed.isEmpty()) return null
        QRData.fromJson(trimmed)?.let { qr -> return qr.token.trim().ifEmpty { null } }
        return normalizeOtp(trimmed) ?: trimmed
    }

    /**
     * Formateo mientras se escribe: pasa a mayúsculas solo mientras la entrada pueda ser un OTP
     * (hasta 8 alfanuméricos ignorando guiones/espacios). Un token largo o un JSON se dejan intactos.
     */
    fun formatWhileTyping(raw: String): String {
        val compacted = compact(raw)
        val couldBeOtp = compacted.length <= OTP_LENGTH && compacted.all { it.isAsciiLetterOrDigit() }
        return if (couldBeOtp) raw.uppercase() else raw
    }

    /** Texto de caducidad a partir de `expires_at` (ms): "Caduca en 23 h 59 min". */
    fun formatExpiryRemaining(expiresAtMs: Long, nowMs: Long): String {
        val remainingMs = expiresAtMs - nowMs
        if (remainingMs <= 0) return "Caducada"
        val totalMinutes = remainingMs / 60_000
        val hours = totalMinutes / 60
        val minutes = totalMinutes % 60
        return when {
            hours > 0 -> "Caduca en $hours h $minutes min"
            minutes > 0 -> "Caduca en $minutes min"
            else -> "Caduca en menos de 1 min"
        }
    }

    fun isExpired(expiresAtMs: Long, nowMs: Long): Boolean = nowMs >= expiresAtMs

    /** Fecha y hora local de caducidad: "24/10/2026 18:05". */
    @OptIn(ExperimentalTime::class)
    fun formatExpiryDateTime(expiresAtMs: Long, timeZone: TimeZone = TimeZone.currentSystemDefault()): String {
        val dt = Instant.fromEpochMilliseconds(expiresAtMs).toLocalDateTime(timeZone)
        fun Int.pad() = toString().padStart(2, '0')
        return "${dt.day.pad()}/${dt.month.number.pad()}/${dt.year} ${dt.hour.pad()}:${dt.minute.pad()}"
    }

    private fun Char.isAsciiLetterOrDigit(): Boolean =
        this in 'A'..'Z' || this in 'a'..'z' || this in '0'..'9'
}
